import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { FoodsIconButton, FoodsTextInput, ToggleSwitch, Typography } from "@foodpilot/foods";
import { Stack, useTheme, FormControl, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { ProductFormProductIngredient, ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { ProductRefField } from "src/pages/Product/ProductForm/Pages/Page1/Fields/ProductRefField";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { SsqAutocomplete, SsqOption } from "src/components/Selector/SsqSelector";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { ProductPhotoUpload } from "src/pages/Product/components/ProductPhotoUpload";
import { useIsOpenFoodFactsSearchEnabled } from "src/hooks/useIsOpenFoodFactsSearchEnabled";
import { OpenFoodFactsSearchModal } from "src/pages/Product/v2/ProductFormV2/OpenFoodFacts/OpenFoodFactsSearchModal";
import { OpenFoodFactsResolvedProduct } from "src/api/client-api/openFoodFacts/types";
import { useSnackbar } from "src/utils/useSnackbar";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import type { FieldError } from "../validation";

const productReferenceSchema = z.object({
    id: z.number(),
    name: z.string().min(1),
});

const identitySchema = z.object({
    title: z.string().min(1),
    clientId: z.string().min(1),
    productReference: productReferenceSchema.nullish(),
    netWeight: z.number().gt(0),
    ssq_id: z.number().nullable().optional(),
    organic: z.boolean(),
});

type IdentityFormData = z.infer<typeof identitySchema>;

type Step1IdentityProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    disabled?: boolean;
    onPhotoPersistencePendingChange?: (pending: boolean) => void;
    questionnaireScore?: string | null;
    scoreUnit?: string;
    errors?: FieldError[];
};

/**
 * Step 1 - Identity (Figma `12819:67407` "Product creation - Filled / Identity").
 *
 * Layout:
 *  - Top row: square image placeholder (left) + Product name input + Organic toggle (right)
 *  - Associated reference product (full width)
 *  - Bottom row: Unique identifier + Weight (50/50)
 *  - Admin-only SSQ selector at the very end
 *
 * The image upload stores photos on the product. Organic is persisted on Product
 * and wired to the form state.
 *
 * Returns bare section contents — `SectionCard` + `SectionHeader` are provided by
 * `ProductFormV2`.
 */
export const Step1Identity = (props: Step1IdentityProps) => {
    const {
        product,
        setProduct,
        disabled = false,
        onPhotoPersistencePendingChange,
        questionnaireScore = null,
        scoreUnit = "Pts",
        errors: saveErrors = [],
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { isAdmin } = useFoodpilotContext();
    const snackbar = useSnackbar();
    const isAdminUser = isAdmin();
    const isOpenFoodFactsSearchEnabled = useIsOpenFoodFactsSearchEnabled();
    const [isOpenFoodFactsModalOpen, setOpenFoodFactsModalOpen] = useState(false);
    const noSsqSelectedLabel = t("product.v2.identity.no_ssq_selected", "No SSQ selected");
    const dividerStroke = theme.custom.strokes.containerDivider;
    const identityInputSx = {
        "&.MuiInputBase-root": {
            borderColor: dividerStroke.color,
            paddingInline: theme.spacing(2),
            ...theme.typography["body-regular"],
        },
    };
    const { ssq: productSsq, isLoading: isSsqLoading } = useGetOneSsq(product.ssq_id ?? undefined);

    const {
        control,
        reset,
        formState: { errors: formErrors },
    } = useForm<IdentityFormData>({
        resolver: zodResolver(identitySchema),
        mode: "onChange",
        reValidateMode: "onBlur",
        defaultValues: {
            title: product.title ?? "",
            clientId: product.clientId ?? "",
            productReference: product.productReference ?? undefined,
            netWeight: product.netWeight ?? undefined,
            ssq_id: product.ssq_id ?? null,
            organic: product.organic ?? false,
        },
    });

    const hasSaveError = (field: string) => saveErrors.some((error) => error.field === field);
    const titleHasError = Boolean(formErrors.title) || (hasSaveError("title") && !product.title?.trim());
    const clientIdHasError = Boolean(formErrors.clientId) || (hasSaveError("clientId") && !product.clientId?.trim());
    const netWeightHasError =
        Boolean(formErrors.netWeight) ||
        (hasSaveError("netWeight") && !(typeof product.netWeight === "number" && product.netWeight > 0));
    const validationErrorMessage = t("form.mandatory_field", "*Mandatory fields");

    useEffect(() => {
        reset({
            title: product.title ?? "",
            clientId: product.clientId ?? "",
            productReference: product.productReference ?? undefined,
            netWeight: product.netWeight ?? undefined,
            ssq_id: product.ssq_id ?? null,
            organic: product.organic ?? false,
        });
    }, [
        product.clientId,
        product.netWeight,
        product.organic,
        product.productReference,
        product.ssq_id,
        product.title,
        reset,
    ]);

    const handleOpenFoodFactsSelect = async (resolvedProduct: OpenFoodFactsResolvedProduct) => {
        let importedImage: File | null = null;
        const shouldImportImage = resolvedProduct.imageUrl !== null;

        if (shouldImportImage && resolvedProduct.imageUrl !== null) {
            try {
                const response = await fetch(resolvedProduct.imageUrl);
                const blob = await response.blob();
                importedImage = new File([blob], `open-food-facts-${resolvedProduct.code}.jpg`, {
                    type: blob.type || "image/jpeg",
                });
            } catch {
                importedImage = null;
            }
        }

        setProduct((prevState) => {
            const resolvedIngredients = resolvedProduct.ingredients.flatMap<ProductFormProductIngredient>(
                (ingredient) => {
                    if (ingredient.ingredientReference === null) {
                        return [];
                    }

                    const ingredientQuantity =
                        resolvedProduct.netWeight !== null && ingredient.percent !== null ?
                            Number(((resolvedProduct.netWeight * ingredient.percent) / 100).toFixed(3))
                        :   null;

                    return [
                        {
                            ingredientReference: {
                                compositeId: ingredient.ingredientReference.compositeId,
                                id: ingredient.ingredientReference.id,
                                source: ingredient.ingredientReference.source,
                                name: ingredient.ingredientReference.name,
                                defaultOrigin: ingredient.ingredientReference.defaultOrigin,
                                fresh: ingredient.ingredientReference.fresh,
                            },
                            quantity: ingredientQuantity,
                            usedQuantity: ingredientQuantity,
                            origin: ingredient.ingredientReference.defaultOrigin,
                        },
                    ];
                },
            );

            return {
                ...prevState,
                title: resolvedProduct.title,
                clientId: resolvedProduct.code,
                netWeight: resolvedProduct.netWeight ?? prevState.netWeight,
                productReference:
                    resolvedProduct.productReference !== null ?
                        {
                            id: resolvedProduct.productReference.id,
                            name: resolvedProduct.productReference.name,
                        }
                    :   null,
                productIngredients: resolvedIngredients,
                organic: resolvedProduct.organic ?? prevState.organic,
                pendingProductPhotos: importedImage !== null ? [importedImage] : [],
                photos: importedImage !== null ? prevState.photos : [],
            };
        });

        const matchedIngredients = resolvedProduct.ingredients.length - resolvedProduct.unmatchedIngredientCount;
        snackbar.setSnackbarProps({
            type: importedImage === null && shouldImportImage ? "warning" : "success",
            message:
                importedImage === null && shouldImportImage ?
                    t(
                        "product.openFoodFacts.prefillSuccessWithoutImage",
                        "Pre-filled from Open Food Facts ({{matched}} / {{total}} ingredients matched). Product image could not be downloaded.",
                        {
                            matched: matchedIngredients,
                            total: resolvedProduct.totalIngredientCount,
                        },
                    )
                :   t(
                        "product.openFoodFacts.prefillSuccess",
                        "Pre-filled from Open Food Facts ({{matched}} / {{total}} ingredients matched)",
                        {
                            matched: matchedIngredients,
                            total: resolvedProduct.totalIngredientCount,
                        },
                    ),
        });
        setOpenFoodFactsModalOpen(false);
    };

    return (
        <Stack padding={theme.spacing(3)} gap={theme.spacing(3)}>
            <Stack direction={{ xs: "column", md: "row" }} gap={theme.spacing(2)} alignItems="flex-start">
                <ProductPhotoUpload
                    product={product}
                    setProduct={setProduct}
                    disabled={disabled}
                    onPersistencePendingChange={onPhotoPersistencePendingChange}
                    appearance="product-v2"
                    variant="compact"
                />

                <Stack flex={1} gap={theme.spacing(2)} minWidth={0} width="100%">
                    <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection="column" gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">{`${t("product.identity.name", "Product name")}*`}</Typography>
                                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                                    <Box flex={1} minWidth={0}>
                                        <FoodsTextInput
                                            {...field}
                                            error={titleHasError}
                                            ExtraInputProps={{
                                                inputProps: {
                                                    "aria-label": t("product.identity.name", "Product name"),
                                                    "aria-invalid": titleHasError,
                                                    "aria-describedby":
                                                        titleHasError ? "product-v2-title-error" : undefined,
                                                },
                                                sx: identityInputSx,
                                            }}
                                            onChange={(value: string) => {
                                                field.onChange(value);
                                                setProduct((prevState) => ({ ...prevState, title: value ?? null }));
                                            }}
                                        />
                                        {titleHasError && (
                                            <Typography
                                                id="product-v2-title-error"
                                                role="alert"
                                                variant="body-regular"
                                                sx={{ color: theme.palette.error.main }}
                                            >
                                                {validationErrorMessage}
                                            </Typography>
                                        )}
                                    </Box>
                                    {isOpenFoodFactsSearchEnabled && (
                                        <FoodsIconButton
                                            icon="search"
                                            size="m"
                                            withBackground
                                            buttonProps={{
                                                "aria-label": t(
                                                    "product.openFoodFacts.search.title",
                                                    "Search Open Food Facts",
                                                ),
                                                onClick: () => setOpenFoodFactsModalOpen(true),
                                                type: "button",
                                                sx: {
                                                    flexShrink: 0,
                                                },
                                            }}
                                            tooltip={{
                                                text: t("product.openFoodFacts.search.title", "Search Open Food Facts"),
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        )}
                    />

                    <Controller
                        name="organic"
                        control={control}
                        render={({ field }) => {
                            const organicLabel = t("product.v2.identity.organic_product", "Organic product");

                            return (
                                <ToggleSwitch
                                    checked={field.value ?? false}
                                    withStateIcons
                                    inputProps={{ "aria-label": organicLabel }}
                                    label={
                                        <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                                            {organicLabel}
                                        </Typography>
                                    }
                                    onChange={(_event, checked) => {
                                        field.onChange(checked);
                                        setProduct((prevState) => ({ ...prevState, organic: checked }));
                                    }}
                                />
                            );
                        }}
                    />
                </Stack>
            </Stack>

            <Controller
                name="productReference"
                control={control}
                render={({ field }) => (
                    <ProductRefField
                        productReference={product.productReference}
                        appearance="product-v2"
                        label={t("product.v2.identity.associated_reference_product", "Associated reference product")}
                        placeholder={t("product.v2.identity.select_reference", "Select a reference")}
                        setNewProductRef={(value) => {
                            field.onChange(value);
                            setProduct((prevState) => ({ ...prevState, productReference: value ?? null }));
                        }}
                    />
                )}
            />

            <Stack direction={{ xs: "column", md: "row" }} gap={theme.spacing(2)}>
                <Box flex={1} minWidth={0}>
                    <Controller
                        name="clientId"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection="column" gap={theme.spacing(1)}>
                                <Typography variant="body-semibold">
                                    {t("product.v2.identity.unique_identifier", "Unique identifier")}*
                                </Typography>
                                <FoodsTextInput
                                    {...field}
                                    error={clientIdHasError}
                                    ExtraInputProps={{
                                        inputProps: {
                                            "aria-label": t(
                                                "product.v2.identity.unique_identifier",
                                                "Unique identifier",
                                            ),
                                            "aria-invalid": clientIdHasError,
                                            "aria-required": true,
                                            "aria-describedby":
                                                clientIdHasError ? "product-v2-client-id-error" : undefined,
                                        },
                                        sx: identityInputSx,
                                    }}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setProduct((prevState) => ({
                                            ...prevState,
                                            clientId: value ?? null,
                                        }));
                                    }}
                                />
                                {clientIdHasError && (
                                    <Typography
                                        id="product-v2-client-id-error"
                                        role="alert"
                                        variant="body-regular"
                                        sx={{ color: theme.palette.error.main }}
                                    >
                                        {validationErrorMessage}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    />
                </Box>

                <Box flex={1} minWidth={0}>
                    <Controller
                        name="netWeight"
                        control={control}
                        render={({ field }) => (
                            <Stack flexDirection="column" gap={theme.spacing(1)}>
                                <Stack direction="row" alignItems="baseline" gap={theme.spacing(0.5)}>
                                    <Typography component="span" variant="body-semibold">
                                        {t("product.v2.identity.weight", "Weight")}
                                    </Typography>
                                    <Typography component="span" variant="body-regular">
                                        {t(
                                            "product.v2.identity.weight_without_packaging",
                                            "(product weight without packaging)",
                                        )}
                                        <Typography component="span" variant="body-semibold">
                                            *
                                        </Typography>
                                    </Typography>
                                </Stack>
                                <FoodsTextInput
                                    {...field}
                                    value={field.value?.toString() ?? ""}
                                    error={netWeightHasError}
                                    onChange={(value: string) => {
                                        field.onChange(+value);
                                        setProduct((prevState) => ({ ...prevState, netWeight: +value }));
                                    }}
                                    unit={t("weight.grams", "g")}
                                    ExtraInputProps={{
                                        inputProps: {
                                            "aria-label": t("product.v2.identity.weight", "Weight"),
                                            "aria-invalid": netWeightHasError,
                                            "aria-describedby":
                                                netWeightHasError ? "product-v2-net-weight-error" : undefined,
                                        },
                                        type: "number",
                                        sx: identityInputSx,
                                    }}
                                />
                                {netWeightHasError && (
                                    <Typography
                                        id="product-v2-net-weight-error"
                                        role="alert"
                                        variant="body-regular"
                                        sx={{ color: theme.palette.error.main }}
                                    >
                                        {validationErrorMessage}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    />
                </Box>
            </Stack>

            {isAdminUser && (
                <Controller
                    name="ssq_id"
                    control={control}
                    render={({ field }) => (
                        <FormControl size="small">
                            <Typography variant="body-semibold" sx={{ paddingBottom: theme.spacing(1) }}>
                                {t("product.identity.ssq", "SSQ")}
                            </Typography>

                            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} gap={2}>
                                <Box flex={1} width="100%" minWidth={0}>
                                    {!isSsqLoading ?
                                        <SsqAutocomplete
                                            appearance="product-v2"
                                            accessibleLabel={t("product.identity.ssq", "SSQ")}
                                            value={product.ssq_id == null ? null : (productSsq ?? null)}
                                            entityType="product"
                                            placeholder={noSsqSelectedLabel}
                                            onChange={(ssq: SsqOption | null) => {
                                                const ssqId = ssq?.id ?? null;
                                                field.onChange(ssqId);
                                                setProduct((currentProduct) => ({
                                                    ...currentProduct,
                                                    ssq_id: ssqId,
                                                }));
                                            }}
                                        />
                                    :   <FoodsLoadingIndicator
                                            label={t("product.identity.ssq.loading", "Loading SSQ")}
                                        />
                                    }
                                </Box>

                                {product.ssq_id != null && (
                                    <FoodsIconButton
                                        icon="close"
                                        size="xs"
                                        buttonProps={{
                                            "aria-label": t("product.v2.identity.clear_ssq", "Clear SSQ"),
                                            onClick: () => {
                                                field.onChange(null);
                                                setProduct((currentProduct) => ({
                                                    ...currentProduct,
                                                    ssq_id: null,
                                                }));
                                            },
                                            type: "button",
                                            sx: { color: theme.custom.grey[2000] },
                                        }}
                                        tooltip={{ text: t("product.v2.identity.clear_ssq", "Clear SSQ") }}
                                    />
                                )}
                            </Stack>
                        </FormControl>
                    )}
                />
            )}

            <OpenFoodFactsSearchModal
                open={isOpenFoodFactsModalOpen}
                initialQuery={product.title ?? ""}
                onClose={() => setOpenFoodFactsModalOpen(false)}
                onSelect={handleOpenFoodFactsSelect}
            />
        </Stack>
    );
};
